/**
 * The branch's clock: the one place v2 reads "now" in the branch's time zone
 * (RESERVATIONS_V2_PLAN.md decisions 9, 29, 32). Catalog's price and schedule rules, printing,
 * the discount sources and reservations all read it here.
 *
 * The zone is the business's (`businessData.timeZone.zoneId`, e.g. "Asia/Kolkata"). With no zone
 * every reading falls back to the terminal's own clock, meaning the time zone the till's device
 * is set to.
 *
 * `ordering/writePath` keeps its own reading on purpose: it is held equal to v1's body by a test
 * (docs/V1_DUPLICATES.md row D2).
 */
import moment, { tz } from "moment-timezone";

/** The business's time zone, or undefined when it has none. */
export function selectBranchZoneId(state: any): string | undefined {
  return state?.businessStore?.businessData?.timeZone?.zoneId;
}

/** The moment in the branch's zone, or on the terminal's clock when there is none (decision 32). */
function inBranchZone(zoneId: string | undefined, now: Date) {
  return zoneId ? tz(now, zoneId) : moment(now);
}

/**
 * `now` as ISO-8601 with the branch's offset, "2026-10-16T11:28:35+05:30". rista-library reads
 * only the date (characters 0 to 10) and the hour and minute (11 to 16) of it.
 */
export function branchTimeOf(zoneId?: string, now: Date = new Date()): string {
  return inBranchZone(zoneId, now).format();
}

/**
 * How the backend writes a reservation's times: the branch's wall clock to the minute, with no
 * zone (docs/architecture/reservations/backend.md §7.1).
 */
export const WALL_CLOCK_FORMAT = "YYYY-MM-DDTHH:mm";

/** `now` on the branch's wall clock, "2026-10-16T11:28". */
export function branchWallClockOf(zoneId?: string, now: Date = new Date()): string {
  return inBranchZone(zoneId, now).format(WALL_CLOCK_FORMAT);
}

/** "Now" in one branch's zone: what a rule is handed instead of reading the clock itself. */
export interface BranchClock {
  /** The business's time zone; undefined when it has none and the terminal's clock is used. */
  readonly zoneId: string | undefined;
  /** Now as ISO-8601 with the branch's offset: catalog's form. */
  iso(): string;
  /** Now on the branch's wall clock to the minute: reservations' form. */
  wallClock(): string;
}

/**
 * "Now" in one branch's zone, read from a source a test can replace. Each read asks the source
 * again, so a clock made once keeps telling the current time.
 */
export function createBranchClock(
  zoneId: string | undefined,
  now: () => Date = () => new Date(),
): BranchClock {
  return {
    zoneId,
    iso: () => branchTimeOf(zoneId, now()),
    wallClock: () => branchWallClockOf(zoneId, now()),
  };
}

/** A clock stopped at one instant, for tests and the gallery. */
export function fixedClock(zoneId: string | undefined, at: Date | string): BranchClock {
  const instant = new Date(at);
  return createBranchClock(zoneId, () => instant);
}
